/* @flow */

import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import { translate } from 'react-i18next';
import NumberFormat from 'react-number-format';

//----------------------------------------------------------------------//
// MATERIAL UI                                                          //
//----------------------------------------------------------------------//

import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';

//----------------------------------------------------------------------//
//----------------------------------------------------------------------//

import getCurrencySymbol from 'utils/getCurrencySymbol';
import ChipList from 'components/ChipList/ChipList';
import type { TypeManufacturersProductsStore } from 'stores/ManufacturersProductsStore.js';

import styles from '../ManufacturerDetails/ManufacturerDetails.module.scss';
import NavigationService from 'services/NavigationService';

type TypeProps = {
  manufacturersProductsStore: TypeManufacturersProductsStore,
};

const statusClassObj: Object = {
  approved: 'approved',
  'not approved': 'not-approved',
  'pending review': 'pending',
  archived: 'archived',
};

@translate()
@inject('manufacturersProductsStore')
@observer
export default class ProductDetails extends React.Component<*> {
  renderAccounts() {
    const {
      manufacturersProductsStore: { selectedProduct: product },
      t,
    } = this.props;
    const chips = product.accounts.map(account => ({
      label: `${account.name} ${account.account_no}`,
    }));

    if (chips.length) {
      return <ChipList chips={chips} />;
    }

    return t('general:none');
  }

  render(): React.Node {
    const { manufacturersProductsStore, t }: TypeProps = this.props;

    const product: any = this.props.manufacturersProductsStore.selectedProduct;
    const statusClass: string = statusClassObj[product.status.toLowerCase()];

    const activeUser: Object = JSON.parse(localStorage.getItem('user'));
    // Price formatting separators, checks for 1,234,567.89
    // This will need revisiting if any other formats are added
    const thousandSeparator: string = activeUser.price_format.match(/^\d+(,\d{3})*(\.\d{1,2})?$/g)
      ? ','
      : '.';
    const decimalSeparator: string = activeUser.price_format.match(/^\d+(,\d{3})*(\.\d{1,2})?$/g)
      ? '.'
      : ',';

    return (
      <React.Fragment>
        {product.status && product.status === 'Pending Review' ? (
          <table className={styles.details}>
            <tbody>
              <tr>
                <td>
                  <b>{t('general:labels.status')}:</b>
                </td>
                <td>
                  <span className={styles[statusClass]}>{product.status}</span>
                </td>
              </tr>
              <tr>
                <td>
                  <b>{t('general:manufacturer')}:</b>
                </td>
                <td>{t('general:messages.info_required')}</td>
              </tr>
              <tr>
                <td>
                  <b>{t('general:model')}:</b>
                </td>
                <td>{t('general:messages.info_required')}</td>
              </tr>
              <tr>
                <td>
                  <b>{t('general:products.labels.base_model')}:</b>
                </td>
                <td>{t('general:messages.info_required')}</td>
              </tr>
              <tr>
                <td>
                  <b>{t('general:description')}:</b>
                </td>
                <td>{t('general:messages.info_required')}</td>
              </tr>
              <tr>
                <td>
                  <b>{t('general:labels.fair_market_value')}:</b>
                </td>
                <td>{t('general:messages.info_required')}</td>
              </tr>
              <tr>
                <td>
                  <b>{t('general:products.labels.perfect_product_number')}:</b>
                </td>
                <td>{product.perfect_number}</td>
              </tr>
              <tr>
                <td>
                  <b>{t('general:products.labels.d365_product_number')}:</b>
                </td>
                <td>{product.d365_number}</td>
              </tr>
              <tr>
                <td>
                  <b>{t('general:products.labels.global_list')}:</b>
                </td>
                {product.global_list ? <td>{t('general:yes')}</td> : <td>{t('general:no')}</td>}
              </tr>
              <tr>
                <td>
                  <b>{t('general:products.labels.accounts')}:</b>
                </td>
                {this.renderAccounts()}
              </tr>
            </tbody>
          </table>
        ) : (
          <table className={styles.details}>
            <tbody>
              <tr>
                <td>
                  <b>{t('general:labels.status')}:</b>
                </td>
                <td>
                  <span className={styles[statusClass]}>{product.status}</span>
                </td>
              </tr>
              <tr>
                <td>
                  <b>{t('general:manufacturer')}:</b>
                </td>
                <td>{manufacturersProductsStore.selectedManufacturer.name}</td>
              </tr>
              <tr>
                <td>
                  <b>{t('general:model')}:</b>
                </td>
                <td>{product.model}</td>
              </tr>
              <tr>
                <td>
                  <b>{t('general:products.labels.base_model')}:</b>
                </td>
                <td>{product.base_model}</td>
              </tr>
              <tr>
                <td>
                  <b>{t('general:description')}:</b>
                </td>
                <td>{product.product_group_description}</td>
              </tr>
              <tr>
                <td>
                  <b>{t('general:labels.fair_market_value')}:</b>
                </td>
                <td>
                  {
                    <NumberFormat
                      value={product.fair_market_value}
                      displayType="text"
                      prefix={getCurrencySymbol('', 'dollars')}
                      decimalScale={2}
                      thousandSeparator={thousandSeparator}
                      decimalSeparator={decimalSeparator}
                    />
                  }
                </td>
              </tr>
              <tr>
                <td>
                  <b>{t('general:products.labels.perfect_product_number')}:</b>
                </td>
                <td>{product.perfect_number}</td>
              </tr>
              <tr>
                <td>
                  <b>{t('general:products.labels.d365_product_number')}:</b>
                </td>
                <td>{product.d365_number}</td>
              </tr>
              <tr>
                <td>
                  <b>{t('general:products.labels.global_list')}:</b>
                </td>
                {product.global_list ? <td>{t('general:yes')}</td> : <td>{t('general:no')}</td>}
              </tr>
              <tr>
                <td>
                  <b>{t('general:products.labels.accounts')}:</b>
                </td>
                {this.renderAccounts()}
              </tr>
            </tbody>
          </table>
        )}
        <Divider />
        <List className={styles.actions}>
          <ListItem disableGutters={true}>
            <ListItemText
              primary={
                <Typography component="span" variant="title">
                  {product.assets_count}
                </Typography>
              }
              secondary={t('general:products.messages.asset_created', {
                count: product.assets_count,
              })}
            />
            <Link
              to={NavigationService.formulateReportViewUri('/assets', {
                page: 1,
                filters: [
                  { key: 'model', values: [product.id] },
                  { key: 'asset_type', values: ['Rented Open', 'Owned'] },
                ],
              })}
              className="layout-button"
              style={product.assets_count === 0 ? { pointerEvents: 'none' } : null}
            >
              <Button variant="contained" color="primary" disabled={product.assets_count === 0}>
                {t('general:actions.view_asset', { count: product.assets_count })}
              </Button>
            </Link>
          </ListItem>
        </List>
      </React.Fragment>
    );
  }
}
